<template>
  <div class="percentage-complete-block">
    <div v-if="loading" class="block-loading">
      <div class="block-header">
        <span class="block-title">{{ title }}</span>
      </div>
      <div class="block-loading-spinner">
        <div class="spinner"></div>
        <span class="block-loading-text">Loading...</span>
      </div>
    </div>
    <template v-else>
      <div class="block-meta">
        <div class="block-header">
          <span class="block-title">{{ title }}</span>
          <div class="block-average">
            <span class="block-average-value" :style="{ color: activeColor }">{{ averagePercent }}%</span>
            <span class="block-average-label">complete</span>
          </div>
        </div>
        <div class="block-track">
          <div
            class="block-bar"
            :style="{ width: averagePercent + '%', backgroundColor: activeColor }"
          />
        </div>
        <p v-if="description" class="block-description">{{ description }}</p>
      </div>
      <div class="block-items">
        <percentage-complete
          v-for="(item, index) in percentageCompletes"
          :key="index"
          :title="item.title"
          :percent="item.percent"
          :color="item.color"
        />
      </div>
      <div class="children">
        <slot />
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import PercentageComplete from '@/components/ui/percentage-complete.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: null,
  },
  color: {
    type: String,
    default: '#f69641',
  },
  completeColor: {
    type: String,
    default: '#a0c41c',
  },
  percentageCompletes: {
    type: Array,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const averagePercent = computed(() => {
  if (!props.percentageCompletes.length) return 0;
  const total = props.percentageCompletes.reduce((sum, item) => sum + item.percent, 0);
  return Math.round(total / props.percentageCompletes.length);
});

const activeColor = computed(() => {
  return averagePercent.value >= 100 ? props.completeColor : props.color;
});
</script>

<style scoped lang="scss">
.percentage-complete-block {
  width: 100%;
  padding: 35px 50px;
}

.block-meta {
  margin-bottom: 40px;
}

.block-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.block-title {
  font-weight: 600;
  font-size: 1.2rem;
}

.block-average {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.block-average-value {
  font-size: 1.6rem;
  line-height: 1.6rem;
}

.block-average-label {
  font-size: 1rem;
  color: #666;
}

.block-track {
  width: 100%;
  height: 12px;
  background: #e5e7eb;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 16px;
}

.block-bar {
  height: 100%;
  border-radius: 6px;
  transition: width 0.3s ease;
}

.block-description {
  font-size: 14px;
  color: #666;
  margin-bottom: 24px;
}

.block-items {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.block-loading {
  width: 100%;
}

.block-loading-spinner {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
}

.block-loading-text {
  font-size: 14px;
  color: #666;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-top-color: #666;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.children {
  margin-top: 60px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
